import { useState } from "react";
import { X, Copy, Check, Printer, Mail, KeyRound } from "lucide-react";
import toast from "react-hot-toast";

export interface PortalCredentials {
  portalUrl: string;
  username: string;
  tempPassword: string;
}

interface Props {
  credentials: PortalCredentials;
  clientName?: string;
  /** True when a welcome email was already delivered to the client. */
  emailSent?: boolean;
  onClose: () => void;
}

// Manual credential delivery — shown after a Client Workspace account is
// provisioned so the Business Developer can copy, print/save, or defer sending
// (e.g. share over Upwork / WhatsApp when the contact has no email on file).
const CredentialsModal = ({ credentials, clientName, emailSent, onClose }: Props) => {
  const [copied, setCopied] = useState(false);

  const asText = () =>
    [
      `Omni Workspace Client Portal — Login Details${clientName ? ` for ${clientName}` : ""}`,
      ``,
      `Portal URL: ${credentials.portalUrl}`,
      `Username:   ${credentials.username}`,
      `Temporary Password: ${credentials.tempPassword}`,
      ``,
      `You'll be asked to set a new password on first login.`,
    ].join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText());
      setCopied(true);
      toast.success("Credentials copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  // Print / Save as PDF — opens a clean printable sheet; the browser's print
  // dialog offers "Save as PDF". Avoids pulling in a PDF library.
  const print = () => {
    const w = window.open("", "_blank", "width=600,height=700");
    if (!w) {
      toast.error("Popup blocked — allow popups to print");
      return;
    }
    w.document.write(`<!doctype html><html><head><title>Client Portal Credentials</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#1f2937;padding:40px;}
        h1{color:#0058c3;font-size:20px;}
        .row{margin:10px 0;font-size:15px;}
        .label{color:#6b7280;font-size:12px;text-transform:uppercase;letter-spacing:.05em;}
        .box{border:1px solid #dbe7ff;background:#f8faff;border-radius:10px;padding:20px;margin-top:16px;}
        .note{color:#6b7280;font-size:12px;margin-top:20px;}
      </style></head><body>
      <h1>Omni Workspace Client Portal — Login Details</h1>
      ${clientName ? `<p>Prepared for <strong>${clientName}</strong></p>` : ""}
      <div class="box">
        <div class="row"><div class="label">Portal URL</div>${credentials.portalUrl}</div>
        <div class="row"><div class="label">Username</div><strong>${credentials.username}</strong></div>
        <div class="row"><div class="label">Temporary Password</div><strong>${credentials.tempPassword}</strong></div>
      </div>
      <p class="note">For security, you will be asked to set a new password the first time you sign in.</p>
      </body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 grid place-items-center text-[#0058c3]"><KeyRound size={18} /></div>
            <div>
              <h2 className="text-[16px] font-bold text-gray-800 leading-tight">Client Portal Access</h2>
              <p className="text-[11px] text-gray-500">Account created{clientName ? ` for ${clientName}` : ""}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {emailSent && (
            <div className="flex items-center gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <Mail size={14} /> A welcome email with these details was sent to the client.
            </div>
          )}

          <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 space-y-3">
            <Field label="Portal URL" value={credentials.portalUrl} mono />
            <Field label="Username" value={credentials.username} mono />
            <Field label="Temporary Password" value={credentials.tempPassword} mono />
          </div>

          <p className="text-[11px] text-gray-500">
            The client must change this temporary password on first login. Share it securely
            (e.g. Upwork message, WhatsApp) if no email is on file.
          </p>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={copy} className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50">
              {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />} Copy
            </button>
            <button onClick={print} className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50">
              <Printer size={15} /> Print / Save PDF
            </button>
          </div>
          <button onClick={onClose} className="w-full px-3 py-2.5 rounded-lg bg-[#0058c3] text-white text-[13px] font-semibold hover:bg-[#004b9e]">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</div>
    <div className={`text-[13px] text-gray-900 break-all ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);

export default CredentialsModal;
